import React, { useState } from 'react'
import { Badge } from 'reactstrap'
import { Descriptions, Empty, Table, message, Popconfirm, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCheckToSlot, faDoorOpen, faTimes } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import 'moment/locale/es'

import CompleteAppointmentModal from './CompleteAppointmentModal'
import CancelAppointmentModal from './CancelAppointmentModal'
import Pagination from '../../../components/Pagination'
import CommentsActionButton from '../../Comments/partials/CommentsActionButton'

import { updateAppointment } from '../services'
import { handleError } from '../../../helpers'

export default function AppointmentsTable({ appointments, reload, pagination, setPagination }) {
	const [completeModal, setCompleteModal] = useState(null)
	const [cancelModal, setCancelModal] = useState(null)
	
	const columns = [
		{
			title: 'Fecha de Agendamiento',
			dataIndex: 'start_at',
			render: t => `${t} (${moment(t).fromNow()})`
		},
        {
			title: 'Proveedor',
			dataIndex: 'supplier',
			render: r => r.name
		},
		{
			title: 'Bodega',
			dataIndex: 'warehouse',
			render: r => r.sku
		},
		{
			title: 'Muelle',
			dataIndex: 'warehouse_dock',
			render: r => r.name
		},
		{
			title: 'Ordenes Asociadas',
			dataIndex: 'order_details',
			render: r => {
				const uniqueOrders = [...new Set(r?.map(detail => detail.sku.split('-')[0]))]
				return uniqueOrders.join(', ')
			}
		},
		{
			title: 'Vehículo',
			dataIndex: 'vehicle_type',
			render: r => `${r.name} (${r.unload_time} min)`
		},
		{
			title: 'Estado',
			dataIndex: 'status',
			render: t => <Badge color={t.color}>{t.label}</Badge>
		},
        {
            title: 'Acciones',
			dataIndex: 'id',
			render: (t, r) => (
				<>
					<CommentsActionButton type='appointment' id={t} />
					{ r.status.allowed_statuses.includes('confirmed') && (
						<Popconfirm
							title="¿Estás seguro de querer confirmar esta cita?"
							onConfirm={() => handleUpdate(t, { confirmed_at: moment().format('YYYY-MM-DD HH:mm:ss') })}
							okText="Si, confirmar"
							cancelText="No, volver"
							okButtonProps={{ danger: true }}
						>
							<div className='btn btn-outline-primary mr-5'>
								<Tooltip title="Confirmar cita" placement='bottom'>
									<FontAwesomeIcon icon={faCheck} />
								</Tooltip>
							</div>
						</Popconfirm>
					)}
					{ r.status.allowed_statuses.includes('attended') && (
						<Popconfirm
							title="¿Estás seguro de querer marcar como asistida esta cita?"
							onConfirm={() => handleUpdate(t, { attended_at: moment().format('YYYY-MM-DD HH:mm:ss') })}
							okText="Si, asistida"
							cancelText="No, volver"
							okButtonProps={{ danger: true }}
						>
							<div className='btn btn-outline-primary mr-5'>
								<Tooltip title="Marcar como asistida" placement='bottom'>
									<FontAwesomeIcon icon={faDoorOpen} />
								</Tooltip>
							</div>
						</Popconfirm>
					)}
					{ r.status.allowed_statuses.includes('completed') && (
						<div className='btn btn-outline-primary mr-5' onClick={() => setCompleteModal(r)}>
							<Tooltip title="Marcar como completada" placement='bottom'>
								<FontAwesomeIcon icon={faCheckToSlot} />
							</Tooltip>
						</div>
					)}
					{ r.status.allowed_statuses.includes('canceled') && (
						<div className='btn btn-outline-primary mr-5' onClick={() => setCancelModal(r)}>
							<Tooltip title="Cancelar cita" placement='bottom'>
								<FontAwesomeIcon icon={faTimes} />
							</Tooltip>
						</div>
					)}
				</>
			)
        }
	]

	const expandedRowRender = r => (
		<React.Fragment>
			<Descriptions bordered size='small'>
				<Descriptions.Item label="ID Interno">{r.id}</Descriptions.Item>
				<Descriptions.Item label="Fecha Inicio Cita">
					{r.start_at} ({moment(r.start_at).fromNow()})
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Finalización de Cita">
					{r.end_at} ({moment(r.end_at).fromNow()})
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Creación">
					{r.created_at} ({moment(r.created_at).fromNow()})
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Cancelación">
					{r.canceled_at ? r.canceled_at : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Confirmación">
					{r.confirmed_at ? r.confirmed_at : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Ingreso Real">
					{r.attended_at ? r.attended_at : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Fecha Salida Real" span={2}>
					{r.completed_at ? r.completed_at : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Comentarios al finalizar">
					{r.completed_comments ? r.completed_comments : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Comentarios al cancelar" span={2} >
					{r.canceled_comments ? r.canceled_comments : 'N/A'}
				</Descriptions.Item>
				<Descriptions.Item label="Articulos asociados" span={3}>
					{r.order_details.map(detail => (
						<div key={detail.id}>
							[{detail.sku}] {detail.article_name} ({detail.reception_planned_at})
						</div>
					))}
				</Descriptions.Item>
			</Descriptions>
		</React.Fragment>
	)

	const handleUpdate = (id, data) => {
		updateAppointment(id, data)
			.then(res => {
				message.success(res.data.message)
				reload()
			})
			.catch(error => handleError(error))
	}
	
	return (
		<React.Fragment>
			<Table 
				dataSource={appointments}
				rowKey={r => r.id}
				pagination={false}
				columns={columns}
				loading={!appointments}
				size="small"
				expandable={{ expandedRowRender }}
				locale={{ emptyText: (
					<Empty
						description="No se encontraron registros" 
						imageStyle={{ height: '80px' }}
					/> 
				)}}
			/>
			<Pagination 
				pagination={pagination}
				setPagination={setPagination}
				reload={reload}
				className="mb-0"
			/>
			{ completeModal && (
				<CompleteAppointmentModal
					visible
					onClose={() => setCompleteModal(null)}
					appointment={completeModal}
					reload={reload}
				/>
			)}
			{ cancelModal && (
				<CancelAppointmentModal
					visible
					onClose={() => setCancelModal(null)}
					appointment={cancelModal}
					reload={reload}
				/>
			)}
		</React.Fragment>
	)
}